import { createCompareSelection } from "@/composables/useCompareSelection";

// Cap on how many projects can be compared side by side — mirrors MAX_LIBRARY_COMPARE (RTLCA-5094):
// beyond this the compare modal becomes an unreadably wide column-per-project grid. Enforced on the
// add path only; removals always succeed.
export const MAX_PROJECT_COMPARE = 6;

// A project held in the selection/compare set. The name travels with the id so the compare tray can
// render a chip for it without looking the project back up.
export interface ProjectCompareItem {
  id: string;
  name: string;
}

// Module-level singleton so the per-card compare checkbox (toggles projects in/out) and the compare
// tray (renders/removes chips) share one source of truth without prop-drilling. Built on the generic
// capped compare-selection factory (RTLCA-5094) — mirrors useLibrarySelection: the per-card checkbox is
// the single way to build a comparison, there is no separate "selection mode".
const selection = createCompareSelection<ProjectCompareItem>();

// Add/remove a project from the compare set, applying the project-specific cap. Returns false when an
// *add* was rejected because the cap was already reached, so callers can surface a "limit reached" hint.
function toggleItem(project: ProjectCompareItem) {
  return selection.toggleItem(project, MAX_PROJECT_COMPARE);
}

export function useProjectSelection() {
  return {
    // state
    selectedItems: selection.selectedItems,
    selectedIds: selection.selectedIds,
    selectedCount: selection.selectedCount,

    // actions
    toggleItem,
    removeItem: selection.removeItem,
    clearSelection: selection.clearSelection,
    isSelected: selection.isSelected,
  };
}
